import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ProductType } from '@app/modules/product-catalog/services/product-catalog.service';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface UpsellOrder {
  uuid: string;
  date: string;
  date_label: string;
  authorised_at: string | null;
  captured_at: string | null;
  amount: Amount;
  requests_expired_at: string | null;
  requests_expired_at_label: string | null;
  products: UpsellDetail[];
  taxes: {
    identifier: string;
    amount: Amount;
  }[];
}

export interface UpsellDetail {
  uuid: string;
  type: ProductType;
  name: string;
  quantity?: number;
  captured_at: string | null;
  product_uuid: string;
  variation_uuid: string;
  status: UpsellItemStatus;
  price: Amount;
  total_price?: Amount;
  guest_note?: string;
  requires_approval?: boolean;
  upsell_request_status?: UpsellRequestStatus;
  metadata: {
    [key: string]: any;
  };
}

export enum UpsellItemStatus {
  Unknown = 'unknown',
  Pending = 'pending',
  Authorised = 'authorised',
  AuthorisationPending = 'authorisation_pending',
  Captured = 'captured',
  Refunded = 'refunded',
  Failed = 'failed',
  CapturePending = 'capture_pending',
  RefundPending = 'refund_pending',
  RefundFailure = 'refund_failure',
  Cancelled = 'cancelled',
}

export enum UpsellRequestStatus {
  Pending = 'pending',
  Approved = 'approved',
  Denied = 'denied',
}

export interface Amount {
  amount: number;
  amount_minor: number;
  currency: string;
  formatted_string: string;
  formatted_decimal: string;
}

export interface UpsellPrice {
  amount_in_cents: number;
  currency: string;
  currency_symbol: string;
  formatted_amount: string;
}

export interface ApiResponse<T> {
  data: any;
  meta?: any;
}

@Injectable({
  providedIn: 'root',
})
export class UpsellsService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  fetchUpsellsForReservation(reservationUuid: string): Observable<UpsellOrder[]> {
    return this.http
      .get<ApiResponse<UpsellOrder[]>>(`${this.apiUrl}/guest-portal/${reservationUuid}/upsells/details`)
      .pipe(map((response) => response.data));
  }

  approveUpsells(reservationUuid: string, productUuids: string[]): Observable<UpsellDetail> {
    return this.http
      .post<ApiResponse<UpsellDetail>>(`${this.apiUrl}/guest-portal/${reservationUuid}/upsells/approve`, { identifiers: productUuids })
      .pipe(map((response) => response.data));
  }

  denyUpsells(reservationUuid: string, productUuids: string[]): Observable<UpsellDetail> {
    return this.http
      .post<ApiResponse<UpsellDetail>>(`${this.apiUrl}/guest-portal/${reservationUuid}/upsells/deny`, { identifiers: productUuids })
      .pipe(map((response) => response.data));
  }
}
